import React, { useState, useEffect } from 'react';
import { Search, Building2, Globe } from 'lucide-react';
import { API_URL } from '../config/api';

function CompanyForm({ onSubmit, disabled }) {
  const [company, setCompany] = useState('');
  const [country, setCountry] = useState('');
  const [useWebSearch, setUseWebSearch] = useState(true);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fetch available countries from search prompts
    const fetchCountries = async () => {
      try {
        const response = await fetch(`${API_URL}/api/v1/search-prompts`);
        if (response.ok) {
          const data = await response.json();
          
          // Extract countries from country_prompts
          const countryList = Object.keys(data.country_prompts || {}).sort();
          
          // Add a default "Other" option for countries not in the list
          const allCountries = [...countryList, 'Other'].filter(Boolean);
          
          setCountries(allCountries);
          
          // Set default country
          if (allCountries.length > 0) {
            setCountry(allCountries.includes('United States') ? 'United States' : allCountries[0]);
          }
        }
      } catch (error) {
        console.error('Failed to fetch countries:', error);
        // Fallback to a basic list if the API fails
        const fallbackCountries = [
          'United States', 'United Kingdom', 'Canada', 'Germany', 'France',
          'Sweden', 'Netherlands', 'Switzerland', 'Australia', 'Singapore',
          'Japan', 'China', 'India', 'Brazil', 'Mexico', 'Other'
        ].sort();
        setCountries(fallbackCountries);
        setCountry('United States');
      } finally {
        setLoading(false);
      }
    };

    fetchCountries();
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (company.trim() && country) {
      onSubmit({ 
        company: company.trim(), 
        country,
        use_web_search: useWebSearch
      });
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="h-10 bg-gray-200 rounded mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="h-10 bg-gray-200 rounded mb-4"></div>
          <div className="h-12 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-lg p-6">
      <div className="space-y-4">
        <div>
          <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
            <Building2 className="inline-block mr-2" size={16} />
            Company Name
          </label>
          <input
            id="company"
            type="text"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            placeholder="e.g., Spotify, Tesla, OpenAI"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
            disabled={disabled}
            required
          />
        </div>

        <div>
          <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-2">
            <Globe className="inline-block mr-2" size={16} />
            Country
          </label>
          <select
            id="country"
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
            disabled={disabled || countries.length === 0}
            required
          >
            {countries.length === 0 && (
              <option value="">Loading countries...</option>
            )}
            {countries.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
          {country && (
            <p className="mt-1 text-xs text-gray-500">
              {countries.includes(country) && country !== 'Other' 
                ? 'Using country-specific search prompt' 
                : 'Using default search prompt'}
            </p>
          )}
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <label htmlFor="webSearch" className="flex items-center cursor-pointer">
              <input
                id="webSearch"
                type="checkbox"
                checked={useWebSearch}
                onChange={(e) => setUseWebSearch(e.target.checked)}
                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                disabled={disabled}
              />
              <span className="ml-2 text-sm font-medium text-gray-700">
                Use web search for context
              </span>
            </label>
            <span className="text-xs text-gray-500">
              {useWebSearch ? 'Enabled' : 'Disabled'}
            </span>
          </div>

        </div>

        <button
          type="submit"
          disabled={disabled || !company.trim() || !country}
          className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white font-medium py-3 px-6 rounded-lg hover:from-blue-700 hover:to-blue-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center"
        >
          <Search className="mr-2" size={20} />
          Categorize Company
        </button>
      </div>
    </form>
  );
}

export default CompanyForm;